import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DashboardsDisplayGridRowDataFragment } from '../../../gen-src/queries/DashboardsDisplayGridRowDataFragment';
import {
  DatasourceDownstreamDashboardsQuery,
  DatasourceDownstreamDashboardsQueryVariables,
} from '../../../gen-src/queries/DatasourceDownstreamDashboardsQuery';
import {
  DownstreamDashboardsFilteredByFieldQuery,
  DownstreamDashboardsFilteredByFieldQuery_fieldsConnection_nodes,
  DownstreamDashboardsFilteredByFieldQueryVariables,
} from '../../../gen-src/queries/DownstreamDashboardsFilteredByFieldQuery';
import { DashboardSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceDownstreamDashboardsQueryGql } from '../../queries/DatasourceDownstreamDashboardsQueryGql';
import { DownstreamDashboardsFilteredByFieldQueryGql } from '../../queries/DownstreamDashboardsFilteredByFieldQueryGql';
import { DashboardsDisplayGrid, DashboardsDisplayGridRowData, getRowDataForDashboard } from '../sharedDisplayGrid/DashboardsDisplayGrid';
import { DashboardsValidSortFields } from '../sorting/sortProviders/DashboardsGridSortProvider';

type QueryVariables = DatasourceDownstreamDashboardsQueryVariables;
type QueryResults = DatasourceDownstreamDashboardsQuery;
type QueryResultListNode = DashboardsDisplayGridRowDataFragment;
type GridRowData = DashboardsDisplayGridRowData;

type QueryVariablesWithFilter = DownstreamDashboardsFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DownstreamDashboardsFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].downstreamDashboardsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && getDashboardsConnection(resultData.fieldsConnection.nodes[0]);
}

function getDashboardsConnection(field: DownstreamDashboardsFilteredByFieldQuery_fieldsConnection_nodes) {
  return 'downstreamDashboardsConnection' in field ? field.downstreamDashboardsConnection : null;
}

export type DatasourceDownstreamDashboardsQueryGridProps =
  GridSortProps<DashboardsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredFieldId: string | null;
    readonly rowCount: number | null;
  };

export function DatasourceDownstreamDashboardsQueryGrid(props: DatasourceDownstreamDashboardsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamDashboardsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDashboardsLineageGridMessage();

  return props.filteredFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DownstreamDashboardsFilteredByFieldQueryGql}
      partialQueryVariables={{ selectedFieldId: props.filteredFieldId, orderBy: props.orderBy as DashboardSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDashboard}
    >
      {({ rowStore }) => <DashboardsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatasourceDownstreamDashboardsQueryGql}
        partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as DashboardSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDashboard}
      >
        {({ rowStore }) => <DashboardsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    );
}
